import { Controller } from "@hotwired/stimulus"

// WR-03 (REVIEW.md) — minimal HTML-entity escaper for values that flow into
// the InfoWindow's HTML payload. `null`/`undefined` collapse to the em-dash
// placeholder used elsewhere in UI-05.
const HTML_ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }
function esc(v) {
  if (v == null) return "—"
  return String(v).replace(/[&<>"']/g, c => HTML_ESCAPES[c])
}

// UI-05 — Naver Maps JS API v3 integration.
//
// Auto-registered via the `controllers/index.js` esbuild glob.
//
// Values:
//   path        — Array of [lat, lon, unix_ts] triples (server-decimated ≤ 1000)
//   scheduleId  — schedule.id, used for fetch("/schedules/:id/metric_at.json")
//
// Targets:
//   container   — empty <div> the SDK paints the map into
//
// Behaviour (per CONTEXT D-K + UI-SPEC §UI-05):
//   - connect():    init Map → Polyline + START/END markers + dot markers + fitBounds
//   - marker click: fetch /schedules/:id/metric_at.json + show InfoWindow + emit map:click
//   - disconnect(): remove markers and null the map ref to free memory on Turbo navigation
export default class extends Controller {
  static values  = { path: Array, scheduleId: Number }
  static targets = ["container"]

  // Turbo Drive로 이 페이지에 진입하면 <head>의 Naver SDK <script src>는 이제 막
  // 추가돼 **비동기 로딩 중**이라, connect()가 그보다 먼저 돈다 → naver 미정의 →
  // 지도가 안 그려지고 새로고침(=SDK가 body보다 먼저 파싱됨)해야 떴다(2026-07-24).
  // 그래서 SDK가 준비될 때까지 기다렸다가 초기화한다. 이미 로드돼 있으면 즉시.
  connect() {
    this.whenSdkReady(() => this.initMap())
  }

  whenSdkReady(callback, tries = 0) {
    if (typeof naver !== "undefined" && naver.maps) { callback(); return }
    if (tries >= 100) {   // ~10초 후 포기 (CSP·자격증명 문제)
      console.warn("Naver Maps SDK not loaded (check CSP or credentials)")
      return
    }
    this.sdkTimer = setTimeout(() => this.whenSdkReady(callback, tries + 1), 100)
  }

  initMap() {
    // Default view: all of Korea at zoom 7 (UI-SPEC empty-state baseline).
    const center = new naver.maps.LatLng(36.5, 127.8)
    this.map = new naver.maps.Map(this.containerTarget, { center, zoom: 7 })

    if (this.pathValue.length === 0) return

    const latlngs = this.pathValue.map(([lat, lon]) => new naver.maps.LatLng(lat, lon))

    this.polyline = new naver.maps.Polyline({
      map:          this.map,
      path:         latlngs,
      strokeColor:  "#0e2a47",
      strokeWeight: 3
    })

    // START / END named markers (UI-SPEC §UI-05).
    this.startMarker = new naver.maps.Marker({
      position: latlngs[0],
      map:      this.map,
      title:    "START"
    })
    this.endMarker = new naver.maps.Marker({
      position: latlngs[latlngs.length - 1],
      map:      this.map,
      title:    "END"
    })

    // Per-point dot markers — click reveals InfoWindow + emits map:click.
    this.markers = this.pathValue.map(([lat, lon, ts]) => {
      const m = new naver.maps.Marker({
        position: new naver.maps.LatLng(lat, lon),
        map:      this.map,
        icon: {
          content: '<div style="width:8px;height:8px;border-radius:50%;background:#0e2a47;"></div>',
          anchor:  new naver.maps.Point(4, 4)
        }
      })
      naver.maps.Event.addListener(m, "click", () => this.openPopup(m, ts))
      return m
    })

    // fitBounds covers the entire decimated path.
    const bounds = new naver.maps.LatLngBounds(latlngs[0], latlngs[0])
    latlngs.forEach(ll => bounds.extend(ll))
    this.map.fitBounds(bounds)
  }

  disconnect() {
    // SDK 로드 전에 떠나면 대기 타이머가 사라진 요소에 지도를 그리려 한다 — 취소.
    if (this.sdkTimer) { clearTimeout(this.sdkTimer); this.sdkTimer = null }
    this.markers?.forEach(m => m.setMap(null))
    this.startMarker?.setMap(null)
    this.endMarker?.setMap(null)
    this.polyline?.setMap(null)
    this.map = null
  }

  async openPopup(marker, unix_ts) {
    try {
      const res = await fetch(`/schedules/${this.scheduleIdValue}/metric_at.json?t=${unix_ts}`, {
        headers: { "Accept": "application/json" }
      })
      if (!res.ok) {
        console.warn("metric_at lookup failed:", res.status)
        return
      }
      const data = await res.json()
      // WR-03 (REVIEW.md) — escape every interpolated server value before it
      // is dropped into the InfoWindow's HTML payload. `metric_at.json`
      // currently returns only ISO timestamps + numbers, so no payload is
      // exploitable today, but the moment a string field (host / note / etc)
      // is added the unescaped template would become a DOM-XSS sink. This
      // is defence-in-depth, not a current-vuln fix.
      const html = `
        <div class="p-2 text-sm font-sans">
          <div><b>시각</b>: ${esc(data.time)}</div>
          <div><b>온도</b>: ${esc(data.temp)}℃ &nbsp; <b>습도</b>: ${esc(data.hum)}%</div>
          <div><b>SNU NH3</b>: ${esc(data.snu_nh3)}μA &nbsp; <b>SNU H2S</b>: ${esc(data.snu_h2s)}μA</div>
          <div><b>MP702 NH3</b>: ${esc(data.mp702_nh3)}μA &nbsp; <b>MQ136 H2S</b>: ${esc(data.mq136_h2s)}μA</div>
          <div><b>GPS 시각</b>: ${esc(data.gps_at)}</div>
        </div>`
      new naver.maps.InfoWindow({ content: html }).open(this.map, marker)
      // Emit so chart-map-sync controller (same element, multi-controller) can react.
      this.element.dispatchEvent(new CustomEvent("map:click", {
        detail: { timestamp: unix_ts },
        bubbles: true
      }))
    } catch (err) {
      console.error("openPopup error:", err)
    }
  }
}
